import React from 'react';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import NotInterestedOutlinedIcon from '@mui/icons-material/NotInterestedOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ColorPalette from '../../../../../colorpalette/ColorPalette';
import { GetFriendlyCPF } from '../../../../../utils/Utils';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import FiscalInvoicePopup from '../../../../../popup/FiscalInvoicePopup/FiscalInvoicePopup';
import styles from './ListItem.module.css';

/**
 * Retorna o ícone/texto de status fiscal com base no status da nota mais recente ativa.
 * - issued   → check verde
 * - processing/queued → ampulheta amarela (aguardando)
 * - error → erro vermelho
 * - sem nota → vazio / ícone neutro
 */
function FiscalStatusIcon({ fiscalStatus }) {
    if (fiscalStatus === 'issued') {
        return (
            <Tooltip title="NF emitida">
                <CheckOutlinedIcon htmlColor="#2e7d32" fontSize="small" />
            </Tooltip>
        );
    }
    if (fiscalStatus === 'processing' || fiscalStatus === 'queued') {
        return (
            <Tooltip title="NF em processamento">
                <HourglassEmptyOutlinedIcon htmlColor="#ed6c02" fontSize="small" />
            </Tooltip>
        );
    }
    if (fiscalStatus === 'error') {
        return (
            <Tooltip title="Erro na emissão da NF">
                <NotInterestedOutlinedIcon htmlColor={ColorPalette.red} fontSize="small" />
            </Tooltip>
        );
    }
    // pending / canceled / null → sem nota ativa
    return <span style={{ color: 'var(--color-text-light)', fontSize: '0.8rem' }}>—</span>;
}

/**
 * ListItem — linha da tabela de relatório financeiro.
 *
 * Props:
 * - fiscalStatus: status fiscal vindo diretamente da API (via get_reports_financial.php)
 *   Valores: 'issued' | 'processing' | 'queued' | 'error' | 'canceled' | null
 *   Não faz chamada de API individual — o status já vem no payload do relatório.
 */
const ListItem = ({ id, date, name, cpf, invoiceData, appointmentData, patientData, fiscalStatus: fiscalStatusProp }) => {

    const { UpdateInvoiceNfIssued, GetFiscalInvoiceHistory } = DataProviderInstance();

    const formattedDate = new Date(date).toLocaleDateString('pt-BR');

    // Estado local do campo "issuedDocument" (NF foi emitida manualmente — campo legado)
    // Usa == para compatibilidade com retorno do banco (pode ser int 1 ou string "1")
    const [issuedDocument, setIssuedDocument] = React.useState(
        invoiceData && invoiceData.issuedDocument == "1" ? true : false
    );

    // Estado do popup fiscal
    const [showFiscalPopup, setShowFiscalPopup] = React.useState(false);

    // Status fiscal: começa com o valor vindo da API (prop), pode ser atualizado após fechar o popup
    const [fiscalStatus, setFiscalStatus] = React.useState(fiscalStatusProp ?? null);

    const getCheckedItem = () => (
        <CheckOutlinedIcon htmlColor={ColorPalette.grayDark} fontSize="verysmall" />
    );

    const getUncheckedItem = () => (
        <NotInterestedOutlinedIcon htmlColor={ColorPalette.red} fontSize="verysmall" />
    );

    const toggleIssuedDocument = () => {
        setIssuedDocument(!issuedDocument);
        UpdateInvoiceNfIssued(invoiceData.id, !issuedDocument);
    };

    /**
     * Recarrega o status fiscal após fechar o popup (pode ter emitido/cancelado).
     * Esta é a ÚNICA chamada de API individual — feita apenas quando o usuário
     * interage com o popup, não no carregamento da lista.
     */
    const reloadFiscalStatus = () => {
        if (!appointmentData?.id) return;
        GetFiscalInvoiceHistory(appointmentData.id, (result) => {
            if (result?.success && Array.isArray(result.invoices) && result.invoices.length > 0) {
                const activeInvoice = result.invoices.find(inv =>
                    ['queued', 'processing', 'issued'].includes(inv.status)
                );
                if (activeInvoice) {
                    setFiscalStatus(activeInvoice.status);
                } else {
                    setFiscalStatus('canceled');
                }
            } else {
                setFiscalStatus(null);
            }
        });
    };

    const handleOpenFiscalPopup = () => {
        setShowFiscalPopup(true);
    };

    const handleCloseFiscalPopup = () => {
        setShowFiscalPopup(false);
        // Atualiza o status fiscal após fechar o popup (pode ter emitido/cancelado)
        reloadFiscalStatus();
    };

    // Determina se deve mostrar o botão de NF:
    // Apenas para agendamentos que têm invoiceId (faturados)
    const hasFiscalButton = invoiceData && invoiceData.id;

    return (
        <>
            <tr key={id}>
                <td>{formattedDate}</td>
                <td>{name}</td>
                <td>{GetFriendlyCPF(cpf)}</td>
                {invoiceData
                    ? (invoiceData.paymentMethod === 'credits'
                        // Pago só com sessões de pacote: o valor entrou na compra do pacote
                        ? <td title="Pago com sessões de pacote">Pacote</td>
                        : <td>{Number(invoiceData.price || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>)
                    : <td></td>
                }
                <td>
                    {invoiceData != null ? getCheckedItem() : getUncheckedItem()}
                </td>
                {invoiceData
                    ? <td>{invoiceData.issueDocument == "1" ? getCheckedItem() : getUncheckedItem()}</td>
                    : <td></td>
                }
                {/* Coluna "NF emitida":
                    - toggle manual (issuedDocument) aparece se issueDocument == "1"
                    - FiscalStatusIcon aparece se há status fiscal relevante (issued/processing/error)
                    - fiscalStatus === 'issued' faz override visual: mostra o check mesmo sem toggle */}
                <td style={{ verticalAlign: 'middle' }}>
                    {invoiceData != null && (invoiceData.issueDocument == "1" || fiscalStatus != null) ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            {invoiceData.issueDocument == "1" && (
                                <div
                                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                    onClick={toggleIssuedDocument}
                                >
                                    {(issuedDocument || fiscalStatus === 'issued') ? getCheckedItem() : getUncheckedItem()}
                                </div>
                            )}
                            <FiscalStatusIcon fiscalStatus={fiscalStatus} />
                        </div>
                    ) : (
                        <span></span>
                    )}
                </td>
                {/* Coluna "Nota Fiscal" — ícone de detalhes */}
                <td style={{ verticalAlign: 'middle' }}>
                    {hasFiscalButton && (
                        <div className={styles.fiscalButtonWrapper}>
                            <Tooltip title="Gerenciar nota fiscal">
                                <IconButton
                                    size="small"
                                    onClick={handleOpenFiscalPopup}
                                    sx={{
                                        color: 'var(--color-primary, #1976d2)',
                                        padding: '4px',
                                        '&:hover': {
                                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                        },
                                    }}
                                >
                                    <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </div>
                    )}
                </td>
            </tr>

            {/* Popup de Nota Fiscal */}
            {showFiscalPopup && (
                <FiscalInvoicePopup
                    appointmentData={appointmentData}
                    patientData={patientData}
                    onHide={handleCloseFiscalPopup}
                />
            )}
        </>
    );
};

export default ListItem;
